import { useState } from "react";
import { api } from "@/lib/api";
import { FileSignature, Loader2, X, Download, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * "Preview Contracts" inline panel — renders an Assignment of Contract or
 * Purchase & Sale Agreement against the current lead WITHOUT creating a
 * DocuSign envelope. Wholesalers can eyeball the rendered HTML, copy it,
 * print it, or jump straight to the Send flow on `/app/contracts`.
 */
const TEMPLATES = [
  {
    key: "purchase",
    label: "Purchase Agreement",
    blurb: "Your contract with the seller — lock the property.",
  },
  {
    key: "assignment",
    label: "Assignment of Contract",
    blurb: "Assign equitable interest to your cash buyer.",
  },
];

export default function ContractPreviewButton({ leadId }) {
  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);

  const loadPreview = async (key) => {
    setActiveKey(key);
    setLoading(true);
    setHtml("");
    try {
      const { data } = await api.post(`/docusign/templates/${key}/preview`, { lead_id: leadId });
      setHtml(data.html || "");
    } catch {
      toast.error("Couldn't render preview — check that DocuSign templates are configured.");
    } finally {
      setLoading(false);
    }
  };

  const openWith = (key) => {
    setOpen(true);
    loadPreview(key);
  };

  const printPreview = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  return (
    <>
      <div className="card mb-4" data-testid="contract-preview-panel">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center">
            <FileSignature size={16} />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg leading-tight">Contracts</div>
            <div className="text-xs text-[var(--ink-2)]">Preview against this lead before sending for signature.</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-2.5">
          {TEMPLATES.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => openWith(t.key)}
              className="border border-[var(--line)] rounded-lg px-3 py-2.5 text-left hover:border-[var(--ink)] hover:shadow-sm transition-all"
              data-testid={`contract-preview-${t.key}`}
            >
              <div className="font-bold text-sm flex items-center gap-1.5">
                <FileSignature size={12} /> {t.label}
              </div>
              <div className="text-[11px] text-[var(--ink-3)] mt-0.5">{t.blurb}</div>
            </button>
          ))}
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 md:p-6"
          onClick={() => setOpen(false)}
          data-testid="contract-preview-overlay"
        >
          <div
            className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col"
            style={{ maxHeight: "92vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 p-4 border-b border-[var(--line)]">
              <div className="flex-1">
                <div className="font-display font-bold text-lg">
                  {TEMPLATES.find((t) => t.key === activeKey)?.label || "Contract preview"}
                </div>
                <div className="text-[11px] text-[var(--ink-3)]">Preview only · No envelope sent</div>
              </div>
              <button
                type="button"
                className="btn-ghost"
                onClick={printPreview}
                disabled={!html}
                title="Open in new window for printing"
              >
                <Download size={14} /> Print
              </button>
              <a
                href={`/app/contracts?lead_id=${leadId}&template=${activeKey}`}
                className="btn-money"
                data-testid="contract-preview-send-link"
              >
                <Send size={14} /> Send for signature <ExternalLink size={11} />
              </a>
              <button
                type="button"
                className="rounded-full p-2 hover:bg-[var(--bg)]"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-4 bg-[var(--bg)]">
              {loading ? (
                <div className="text-center py-16 text-[var(--ink-2)]">
                  <Loader2 className="animate-spin mx-auto mb-2" /> Rendering…
                </div>
              ) : html ? (
                <iframe
                  title="Contract preview"
                  srcDoc={html}
                  className="w-full bg-white rounded-lg shadow-md"
                  style={{ height: "70vh", border: "1px solid var(--line)" }}
                  data-testid="contract-preview-iframe"
                />
              ) : (
                <div className="text-center py-16 text-[var(--ink-3)]">Nothing to show yet.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
